import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ctxPluto } from '@/contexts/PlutoContext';
import { ctxPopup } from '@/contexts/PopupContext';
import { ctxRoutes } from '@/contexts/RoutesCtx';
import { useRoutesQuery, type RoutesQuery } from '@/components/routes/api/useRoutesQuery';
import { convertRouteListToSelected } from '@/components/routes/utils/routeSelection';
import { useNotificationStore } from '@/stores/notificationstore';
import { fulldate } from '@/utilities/dateUtils';

export function useRoutesData(): RoutesQuery {
  const plutoCtx = useContext(ctxPluto);
  const popupCtx = useContext(ctxPopup);
  const routesCtx = useContext(ctxRoutes);
  const { t } = useTranslation();
  const showNotification = useNotificationStore((state) => state.showNotification);
  const selectedTarget = routesCtx?.selectedTarget;
  const selectedDates = routesCtx?.selectedDates;
  const setRoutesLoading = routesCtx?.setRoutesLoading;
  const setRoutes = routesCtx?.setRoutes;
  const setPositions = routesCtx?.setPositions;
  const setActualToTime = routesCtx?.setActialToTime;
  const setSelectedRoutes = plutoCtx?.setSelectedRoutes;
  const showLoader = popupCtx?.showLoader;
  const hideLoader = popupCtx?.hideLoader;
  const query = useRoutesQuery({ targetId: selectedTarget?.id, dates: selectedDates });
  const targetId = selectedTarget?.id;
  const fromTime = selectedDates?.from.getTime();
  const toTime = selectedDates?.to.getTime();
  const numberOfRoutes = query.data?.routes.length;
  const originalNoOfRoutes = query.data?.routeMetadata.originalNoOfRoutes;
  const routesLimit = query.data?.routeMetadata.routesLimit;
  const toTimeMicros = query.data?.routeMetadata.toTimeMicros;

  useEffect(() => {
    setRoutesLoading?.(query.isFetching);

    if (!query.isFetching) {
      return;
    }

    showLoader?.('circular');
    return () => hideLoader?.();
  }, [hideLoader, query.isFetching, setRoutesLoading, showLoader]);

  useEffect(() => {
    if (!setRoutes || !setPositions || !setActualToTime || !setSelectedRoutes) {
      return;
    }
    if (!selectedTarget || !selectedDates || !query.isSuccess || query.isFetching || !query.data) {
      return;
    }

    const routes = query.data.routes;
    setRoutes(routes);
    setPositions(query.data.statuses);
    setActualToTime(query.data.actualToTime);
    setSelectedRoutes(convertRouteListToSelected(routes, selectedTarget));
  }, [
    query.data,
    query.isFetching,
    query.isSuccess,
    selectedDates,
    selectedTarget,
    setActualToTime,
    setPositions,
    setRoutes,
    setSelectedRoutes,
  ]);

  useEffect(() => {
    if (
      targetId === undefined ||
      fromTime === undefined ||
      toTime === undefined ||
      !query.isSuccess ||
      query.isFetching ||
      originalNoOfRoutes === undefined ||
      routesLimit === undefined ||
      toTimeMicros === undefined ||
      numberOfRoutes === undefined ||
      originalNoOfRoutes === numberOfRoutes
    ) {
      return;
    }

    // Cache observers can produce equivalent selected objects when the query key changes.
    // Depend on the result values so that this does not repeat the notification.
    showNotification(
      'info',
      t('routes.tooManyRoutes', {
        toTime: fulldate(toTimeMicros, t),
        limit: routesLimit,
        noOfRoutes: originalNoOfRoutes,
        interpolation: { escapeValue: false },
      })
    );
  }, [
    fromTime,
    numberOfRoutes,
    originalNoOfRoutes,
    query.isFetching,
    query.isSuccess,
    routesLimit,
    showNotification,
    t,
    targetId,
    toTime,
    toTimeMicros,
  ]);

  return query;
}
